// Register — iOS grouped-list style. Big total, pill buttons.

import { useMemo, useState } from "react";
import { customers, fmtMoney, items } from "./fixtures";
import { Chevron, MockShell, TouchButton } from "./MockShell";
import { showToast } from "./toast";

type Line = {
  id: string;
  itemId: string;
  qty: number;
  discountPct?: number;
  warning?: string | null;
};

const initialLines: Line[] = [
  { id: "l-1", itemId: "i-4", qty: 2, warning: null },
  { id: "l-2", itemId: "i-1", qty: 1, warning: "Needs lock" },
  { id: "l-3", itemId: "i-7", qty: 1, warning: null },
  { id: "l-4", itemId: "i-8", qty: 1, warning: null },
];

function itemMeta(item: (typeof items)[number]): string {
  const parts: string[] = [];
  if (item.category === "firearm") parts.push("Firearm");
  if (item.ageClass) parts.push(`${item.ageClass}+`);
  return parts.join(" · ");
}

export function Register() {
  const [lines, setLines] = useState<Line[]>(initialLines);
  const [search, setSearch] = useState("");

  const customer = customers.find((c) => c.id === "c-1");

  const subtotal = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const item = items.find((i) => i.id === l.itemId);
        if (!item) return sum;
        const linePrice = item.priceCents * l.qty;
        const afterDiscount = l.discountPct
          ? linePrice - Math.round((linePrice * l.discountPct) / 100)
          : linePrice;
        return sum + afterDiscount;
      }, 0),
    [lines],
  );
  const taxable = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const item = items.find((i) => i.id === l.itemId);
        if (!item?.taxable) return sum;
        const linePrice = item.priceCents * l.qty;
        const afterDiscount = l.discountPct
          ? linePrice - Math.round((linePrice * l.discountPct) / 100)
          : linePrice;
        return sum + afterDiscount;
      }, 0),
    [lines],
  );
  const taxCents = Math.round(taxable * 0.07525);
  const total = subtotal + taxCents;

  const addItem = (itemId: string) => {
    const existing = lines.find((l) => l.itemId === itemId);
    if (existing) {
      setLines(lines.map((l) => (l.id === existing.id ? { ...l, qty: l.qty + 1 } : l)));
    } else {
      const item = items.find((i) => i.id === itemId);
      const warning =
        item && item.category === "firearm" && item.ageClass === 21 ? "Needs lock" : null;
      setLines([...lines, { id: `l-${Date.now()}`, itemId, qty: 1, warning }]);
    }
    setSearch("");
  };

  const removeLine = (id: string) => setLines(lines.filter((l) => l.id !== id));
  const bumpQty = (id: string, delta: number) =>
    setLines(lines.map((l) => (l.id === id ? { ...l, qty: Math.max(1, l.qty + delta) } : l)));

  const filteredSearch = search
    ? items.filter(
        (i) =>
          i.name.toLowerCase().includes(search.toLowerCase()) ||
          i.sku.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  const hasFirearmLine = lines.some((l) => {
    const it = items.find((i) => i.id === l.itemId);
    return it?.category === "firearm";
  });

  return (
    <MockShell title="Register">
      <div className="flex flex-col-reverse lg:flex-row lg:items-start gap-4 lg:gap-6 max-w-7xl mx-auto w-full px-4 sm:px-6 pb-10">
        {/* Ticket column */}
        <section className="flex-1 min-w-0 space-y-4">
          {/* Customer row */}
          <div className="bg-white rounded-2xl overflow-hidden">
            {customer ? (
              <button
                type="button"
                onClick={() => showToast("Would open customer details")}
                className="w-full flex items-center gap-3 px-4 py-3.5 min-h-14 active:bg-slate-50"
              >
                <span className="h-11 w-11 rounded-full bg-slate-900 text-white grid place-items-center text-[15px] font-semibold shrink-0">
                  {customer.name
                    .split(" ")
                    .map((p) => p[0])
                    .join("")
                    .slice(0, 2)}
                </span>
                <span className="flex-1 min-w-0 text-left">
                  <span className="block text-[17px] text-slate-900 break-words">
                    {customer.name}
                  </span>
                  <span className="block text-[13px] text-slate-500 break-words">
                    {customer.phone}
                  </span>
                  <span className="block text-[13px] text-slate-500 break-words">
                    {customer.email}
                  </span>
                  <span className="block text-[12px] text-slate-400 break-words">
                    {customer.address}
                    {customer.hasPermit === "carry" && " · permit to carry"}
                  </span>
                </span>
                <Chevron className="text-slate-300" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => showToast("Would open customer scan")}
                className="w-full flex items-center gap-3 px-4 py-3.5 min-h-14 active:bg-slate-50"
              >
                <span className="text-[17px] text-[#007aff]">Add customer</span>
              </button>
            )}
          </div>

          {/* Lines */}
          <div className="bg-white rounded-2xl overflow-hidden">
            {lines.length === 0 ? (
              <div className="px-4 py-10 text-center text-slate-400">
                Scan or search to add a line
              </div>
            ) : (
              lines.map((line) => {
                const item = items.find((i) => i.id === line.itemId);
                if (!item) return null;
                const linePrice = item.priceCents * line.qty;
                const afterDiscount = line.discountPct
                  ? linePrice - Math.round((linePrice * line.discountPct) / 100)
                  : linePrice;
                const meta = itemMeta(item);
                return (
                  <div key={line.id} className="px-4 py-3 hairline last:shadow-none">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] text-slate-900 tracking-tight leading-snug">
                          {item.name}
                        </div>
                        <div className="text-[13px] text-slate-500 mt-0.5">
                          {meta && <span>{meta} · </span>}
                          {fmtMoney(item.priceCents)}
                        </div>
                        {line.warning && (
                          <div className="mt-1 inline-flex items-center gap-1.5 text-[12px] text-amber-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            {line.warning}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[17px] font-medium tabular-nums text-slate-900">
                          {fmtMoney(afterDiscount)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center bg-slate-100 rounded-full h-9">
                        <button
                          type="button"
                          onClick={() => bumpQty(line.id, -1)}
                          className="h-9 w-9 rounded-full grid place-items-center text-slate-700 active:bg-slate-200"
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="min-w-7 text-center text-[15px] font-medium tabular-nums">
                          {line.qty}
                        </span>
                        <button
                          type="button"
                          onClick={() => bumpQty(line.id, 1)}
                          className="h-9 w-9 rounded-full grid place-items-center text-slate-700 active:bg-slate-200"
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(line.id)}
                        className="text-[13px] text-[#ff3b30] min-h-9 px-2"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Totals */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 hairline">
              <span className="text-[15px] text-slate-500">Subtotal</span>
              <span className="text-[15px] tabular-nums text-slate-900">{fmtMoney(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 hairline">
              <span className="text-[15px] text-slate-500">Tax</span>
              <span className="text-[15px] tabular-nums text-slate-900">{fmtMoney(taxCents)}</span>
            </div>
            <div className="flex items-baseline justify-between px-4 py-4">
              <span className="text-[17px] font-semibold">Total</span>
              <span className="text-[28px] font-semibold tracking-tight tabular-nums">
                {fmtMoney(total)}
              </span>
            </div>
          </div>

          {/* Primary pay button */}
          <TouchButton
            size="lg"
            full
            onClick={() => {
              if (hasFirearmLine) window.location.hash = "#/mocks/firearm-sale";
              else showToast("Would open payment sheet");
            }}
          >
            Pay {fmtMoney(total)}
          </TouchButton>

          <div className="flex gap-2 justify-center">
            <TouchButton
              variant="ghost"
              onClick={() => {
                window.location.hash = "#/mocks/handoff";
              }}
            >
              Park
            </TouchButton>
            <TouchButton
              variant="ghost"
              onClick={() => {
                window.location.hash = "#/mocks/handoff";
              }}
            >
              Send
            </TouchButton>
            <TouchButton variant="ghost" mock="Would open discount picker">
              Discount
            </TouchButton>
          </div>
        </section>

        {/* Right column: scan + quick keys */}
        <aside className="w-full lg:w-80 space-y-4">
          <div className="bg-white rounded-2xl p-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Scan or search"
              className="w-full min-h-11 rounded-xl bg-slate-100 border-0 px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#007aff]/30"
            />
          </div>

          {filteredSearch.length > 0 && (
            <div className="bg-white rounded-2xl overflow-hidden">
              {filteredSearch.slice(0, 5).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => addItem(it.id)}
                  className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 min-h-12 hairline last:shadow-none active:bg-slate-50"
                >
                  <span className="min-w-0">
                    <span className="block text-[15px] text-slate-900 truncate">{it.name}</span>
                    <span className="block text-[13px] text-slate-500">{it.sku}</span>
                  </span>
                  <span className="text-[15px] font-medium tabular-nums shrink-0">
                    {fmtMoney(it.priceCents)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
    </MockShell>
  );
}
